import React, { useState, useEffect } from "react";
import { Card } from "primereact/card";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import backendVerifiedUser from "../../api/backendVerifiedUser";
import SetupLayout from "./SetupLayout";

const countries = [
  { label: "Saudi Arabia (+966)", value: "+966", code: "SA" },
  { label: "United Arab Emirates (+971)", value: "+971", code: "AE" },
  { label: "Kuwait (+965)", value: "+965", code: "KW" },
  { label: "Qatar (+974)", value: "+974", code: "QA" },
  { label: "Bahrain (+973)", value: "+973", code: "BH" },
  { label: "Oman (+968)", value: "+968", code: "OM" },
  { label: "Pakistan (+92)", value: "+92", code: "PK" },
  { label: "India (+91)", value: "+91", code: "IN" },
  { label: "Bangladesh (+880)", value: "+880", code: "BD" },
  { label: "United States (+1)", value: "+1", code: "US" },
  { label: "United Kingdom (+44)", value: "+44", code: "GB" },
  { label: "Canada (+1)", value: "+1", code: "CA" },
  { label: "Australia (+61)", value: "+61", code: "AU" },
];

export default function WidgetSetup() {
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [countryCode, setCountryCode] = useState("+966");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [multilingualEnabled, setMultilingualEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Load current settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await backendVerifiedUser.get("/setup-details");
      console.log("Setup data loaded:", response.data);
      const data = response.data;

      if (data) {
        setWhatsappEnabled(data.whatsapp_enabled === 1 || data.whatsapp_enabled === true);
        setCountryCode(data.whatsapp_country_code || "+966");
        setPhoneNumber(data.whatsapp_number || "");
        setMultilingualEnabled(data.multilingual_enabled === 1 || data.multilingual_enabled === true);
      }
    } catch (error) {
      console.error("Error loading WhatsApp settings:", error);
      setMessage({
        severity: "error",
        text: `Failed to load WhatsApp settings: ${error.message}`,
      });
    }
  };

  const handleSave = async () => {
    // Validation
    if (whatsappEnabled && (!countryCode || !phoneNumber)) {
      setMessage({
        severity: "warn",
        text: "Please select country and enter phone number",
      });
      return;
    }

    // Validate phone number format (digits only)
    if (whatsappEnabled && !/^\d+$/.test(phoneNumber)) {
      setMessage({
        severity: "warn",
        text: "Phone number should contain only digits",
      });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      console.log("Saving WhatsApp settings:", {
        whatsapp_enabled: whatsappEnabled,
        whatsapp_country_code: countryCode,
        whatsapp_number: phoneNumber,
      });

      // First, get all current setup data
      const currentSetup = await backendVerifiedUser.get("/setup-details");
      const setupData = currentSetup.data;

      const formData = new FormData();

      // Add all existing setup fields to prevent data loss
      formData.append("name", setupData.name || "");
      formData.append("email", setupData.email || "");
      formData.append("regNo", setupData.regNo || "");
      formData.append("foundedYear", setupData.foundedYear || "");
      formData.append("country", setupData.country || "");
      formData.append("state", setupData.state || "");
      formData.append("city", setupData.city || "");
      formData.append("address", setupData.address || "");
      formData.append("postalCode", setupData.postalCode || "");
      formData.append("phone", setupData.phone || "");
      formData.append("long", setupData.longi || "");
      formData.append("lat", setupData.lat || "");
      formData.append("colorPrimary", setupData.colorPrimary || "");
      formData.append("colorSecondary", setupData.colorSecondary || "");
      formData.append("fontStyle", setupData.fontStyle || "");
      formData.append("website", setupData.website || "");
      formData.append("description", setupData.description || "");
      formData.append("facebook", setupData.facebook || "");
      formData.append("instagram", setupData.instagram || "");
      formData.append("twitter", setupData.twitter || "");
      // Prefer explicit single/multiple shipping fields; fallback to shipping_charges JSON
      const parseShipping = (s) => {
        try {
          if (s === null || typeof s === 'undefined' || s === '') return { single: 0, multiple: 0 };
          if (typeof s === 'object') {
            return { single: Number(s.single) || 0, multiple: Number(s.multiple) || 0 };
          }
          if (typeof s === 'string' && s.trim().startsWith('{')) {
            const parsed = JSON.parse(s);
            return { single: Number(parsed.single) || 0, multiple: Number(parsed.multiple) || 0 };
          }
          // if it's a plain number, use as single
          const n = Number(String(s).replace(/[^0-9.-]/g, ''));
          return { single: Number.isFinite(n) ? n : 0, multiple: 0 };
        } catch (e) {
          return { single: 0, multiple: 0 };
        }
      };
      const shippingParsed = parseShipping(setupData.shipping_charges);
      formData.append("shipping_charges_single", setupData.shipping_charges_single ?? shippingParsed.single ?? 0);
      formData.append("shipping_charges_multiple", setupData.shipping_charges_multiple ?? shippingParsed.multiple ?? 0);

      // Meta tags
      formData.append("meta_title", setupData.meta_title || "");
      formData.append("meta_description", setupData.meta_description || "");
      formData.append("meta_keywords", setupData.meta_keywords || "");
      formData.append("meta_author", setupData.meta_author || "");
      formData.append("meta_robots", setupData.meta_robots || "");

      // OG tags
      formData.append("og_title", setupData.og_title || "");
      formData.append("og_description", setupData.og_description || "");
      formData.append("og_type", setupData.og_type || "");
      formData.append("og_url", setupData.og_url || "");
      formData.append("og_site_name", setupData.og_site_name || "");

      // Twitter tags
      formData.append("twitter_card", setupData.twitter_card || "");
      formData.append("twitter_title", setupData.twitter_title || "");
      formData.append("twitter_description", setupData.twitter_description || "");

      // Schema
      formData.append("schema_org", setupData.schema_org || "");

      // Featured section
      formData.append("show_featured_home_essentials", setupData.show_featured_home_essentials || "0");

      // WhatsApp widget fields
      formData.append("whatsapp_enabled", whatsappEnabled ? "1" : "0");
      formData.append("whatsapp_country_code", countryCode);
      formData.append("whatsapp_number", phoneNumber);

      // Multilingual
      formData.append("multilingual_enabled", multilingualEnabled ? "1" : "0");

      const response = await backendVerifiedUser.post("/setup-details", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("Save response:", response.data);

      setMessage({
        severity: "success",
        text: "WhatsApp widget settings saved successfully!",
      });
      setTimeout(() => setMessage(null), 3000);

      setTimeout(() => {
        loadSettings();
      }, 1000);
    } catch (error) {
      console.error("Error saving WhatsApp settings:", error);
      const errorMessage = error.response?.data?.error?.msg || error.message || "Unknown error";
      setMessage({
        severity: "error",
        text: `Failed to save WhatsApp settings: ${errorMessage}`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SetupLayout>
      <div className="p-4">
        <Card title="Widget Setup" className="mb-4">
          <div className="p-fluid">
            {/* WhatsApp Widget Section */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-3">WhatsApp Chat Widget</h3>

              {/* Enable/Disable Switch */}
              <div className="field flex align-items-center mb-4">
                <label htmlFor="whatsapp-enabled" className="mr-3 font-medium">
                  Enable WhatsApp Icon
                </label>
                <InputSwitch
                  id="whatsapp-enabled"
                  checked={whatsappEnabled}
                  onChange={(e) => setWhatsappEnabled(e.value)}
                />
              </div>

              {/* Country Code & Phone Number (shown only when enabled) */}
              {whatsappEnabled && (
                <div className="grid">
                  <div className="col-12 md:col-4">
                    <label htmlFor="country-code" className="font-medium mb-2 block">
                      Country Code
                    </label>
                    <Dropdown
                      id="country-code"
                      value={countryCode}
                      options={countries}
                      onChange={(e) => setCountryCode(e.value)}
                      placeholder="Select Country"
                      filter
                      filterBy="label"
                      className="w-full"
                    />
                  </div>

                  <div className="col-12 md:col-8">
                    <label htmlFor="phone-number" className="font-medium mb-2 block">
                      WhatsApp Number (without country code)
                    </label>
                    <InputText
                      id="phone-number"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d]/g, ""))}
                      placeholder="e.g., 501234567"
                      maxLength={15}
                      className="w-full"
                    />
                    <small className="text-muted">
                      Enter only the phone number without country code (digits only)
                    </small>
                  </div>

                  {/* Preview */}
                  {countryCode && phoneNumber && (
                    <div className="col-12 mt-3">
                      <div className="p-3 bg-green-50 border-round">
                        <i className="pi pi-whatsapp text-green-600 mr-2"></i>
                        <span className="font-medium">Full WhatsApp Number: </span>
                        <span className="text-green-700">
                          {countryCode}
                          {phoneNumber}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

            </div>

            {/* Divider */}
            <hr className="my-5" />

            {/* Multilingual Section */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-3">Multilingual Support</h3>

              <div className="field flex align-items-center mb-3">
                <label htmlFor="multilingual-enabled" className="mr-3 font-medium">
                  Enable Language Switcher
                </label>
                <InputSwitch
                  id="multilingual-enabled"
                  checked={multilingualEnabled}
                  onChange={(e) => setMultilingualEnabled(e.value)}
                />
              </div>

              <div className="p-3 bg-blue-50 border-round text-sm">
                <i className="pi pi-info-circle mr-2 text-blue-600"></i>
                {multilingualEnabled
                  ? "Language switcher icon is visible on the frontend — customers can switch languages."
                  : "Language switcher is hidden — website shows in English only."}
              </div>
            </div>

            {/* Info Section */}
            <div className="mt-5 p-3 bg-blue-50 border-round">
              <h4 className="text-md font-semibold mb-2">
                <i className="pi pi-info-circle mr-2"></i>
                How it works
              </h4>
              <ul className="list-disc ml-5 text-sm">
                <li>When enabled, a WhatsApp floating icon will appear on your website's home page</li>
                <li>Customers can click the icon to start a WhatsApp chat with your business</li>
                <li>When disabled, the WhatsApp icon will not be visible to customers</li>
                <li>Make sure to enter a valid WhatsApp business number</li>
              </ul>
            </div>

            {/* Message Display */}
            {message && (
              <div className="mt-4">
                <Message severity={message.severity} text={message.text} className="w-full" />
              </div>
            )}

            {/* Save Button — bottom right */}
            <div className="mt-4" style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                label="Save Widget Settings"
                icon="pi pi-save"
                onClick={handleSave}
                loading={loading}
                className="p-button-success"
              />
            </div>
          </div>
        </Card>
      </div>
    </SetupLayout>
  );
}
